import React, { useState } from 'react'

import {
  View,
  StyleSheet
} from 'react-native'

import { KeyboardAwareScrollView } from '../Components'

import isNumber from 'lodash/isNumber'
import { useContent } from '@sart/content'

import client, { useData } from '@sart/client'

import {
  Description,
  Text,
  Label,
  Space,
  MoonDial,
  TabSelector,
  Slider,
  TextInputExtended,
  Button,
  ErrorMessage
} from '@sart/components'
import { adaptive, typography, colors } from '@sart/styles'
import { useClient } from '@sart/client'
import { prettyErrorMessage, testID } from '@sart/utilities'

import {
  parseTextToValue,
  minMaxLimits,
  balanceCalculator,
  getTransferableBalances,
  parseTextToSlideValue
} from './transferUtils'



export default function Transfer(props) {
  const { navigation, route } = props
  const content = useContent()

  const phoneNumber = route.params?.number
  const [message, setMessage] = useState(null)
  const [{ loading, error }, setData] = useData({ loading: false})

  const {
    cache: {
      accounts,
      balances,
    } = {}
  } = useClient()

  const [type, setType] = React.useState('TEXT')
  const [slide, setSlide] = React.useState(0)
  const [text, setText] = React.useState('')
  const [id, setId] = React.useState('')
  const transferableBalances = getTransferableBalances(balances)
  const tabs = [
    { value: 'TEXT', label: content.swap.texts },
    { value: 'DATA', label: content.swap.data },
    { value: 'MINS', label: content.swap.minutes },
  ]

  const balance = React.useMemo(() => {
    return balanceCalculator(transferableBalances, type, slide)
  }, [transferableBalances, type, slide])

  const isValid = (slide >= balance.min && slide <= balance.max)
  const limits = `${content.swap.minimum} ${balance.formatValue(balance.min)} ${handleSuffixTranslation(balance)}, ${content.swap.maximum} ${balance.formatValue(balance.max)} ${handleSuffixTranslation(balance)}`
  
  React.useEffect(() => {
    setSlide(balance.min)
    setText(balance.formatValue(balance.min))
    setId(balance.id)
  }, [type])
  
  const haveTransformableText = transferableBalances && transferableBalances.text && transferableBalances.text.quantity > 0
  const haveTransformableData = transferableBalances && transferableBalances.data && transferableBalances.data.quantity > 0
  const haveTransformableVoice = transferableBalances && transferableBalances.voice && transferableBalances.voice.quantity > 0
  const noTransferableBalances = !haveTransformableText && !haveTransformableData && !haveTransformableVoice
  
  const result = noTransferableBalances ? content.gift.noCredits : `${content.transfer.send} ${balance.formatValue(slide)} ${handleSuffixTranslation(balance)} ${content.transfer.to} ${phoneNumber}`
  function handleTabChange(tab) {

    setType(tab.value)
  }

  function handleSliderChange(value) {
    setSlide(value)
    setText(balance.formatValue(value))
  }

  function handleTextChange(_text) {
    const text = _text.toUpperCase().trim()
    
    setText(text)
    const value = parseTextToSlideValue(text, type)

    if (isNumber(value)) {
      setSlide(value)
    }
  }

  function getUnits() {
    if(type === 'TEXT') {
      return content.swap.textsDisplayUnits
    } else if(type === 'MINS') {
      return content.swap.minutesDisplayUnits
    } else if(type === 'DATA') {
      return 'MB'
    }
  }
  
  function getAmount(giftAmount) {
    if(type === 'TEXT') {
      return giftAmount
    } else if(type === 'MINS') {
      return giftAmount / 60
    } else if (type === 'DATA') {
      return giftAmount / 1000
    }
  }
  
  async function handleTransfer() {
    setData({ loading: true })
    
    //const values = { to: phoneNumber, type, amount: slide }
    let giftAmount = parseTextToValue(text, type)
    let displayGiftAmount = parseTextToSlideValue(text, type)
    // let units = type
    const values = { balance: {balanceReferenceId: id, quantity:giftAmount, units: getUnits()}, target:{service:{lineIdentifier:phoneNumber, countryCode: '52'}}, communicationDisplayValues: {displayQuantity: getAmount(displayGiftAmount), displayUnits: getUnits()}}
    try {
      const balanceTransferProducts = await client.callApi('catalog','getBalanceTransferProducts',[client.cache.catalogId])

      const balanceTransferProduct = balanceTransferProducts.find((product) => product.productLegacyId === "BALANCE-TRANS-NON-MONETARY-CMP")

      if (!balanceTransferProduct){
        throw new Error(content.gift.notFoundBalanceTransfer)
      }

      const extendedData = values
      const order = await client.checkoutServiceProduct(balanceTransferProduct, extendedData)

      if (!text.match(/^\d{1,}([MG]B)?$/)) {
        throw new Error(content.swap.incorrectBalanceInput) 
      }

      if (order.status === "failed"){
        //order failed, throw an exception to be caught
        throw new Error(content.menu.orderFailed)
      }

      setData({ loading: false })

      const confirmation = {
        title: content.gift.confirm,
        description: `${balance.formatValue(slide)} ${handleSuffixTranslation(balance)} ${content.gift.ofYourOwn} ${phoneNumber} ${content.gift.asGift}`
      }
      
      await client.loadBalancesAndInvoices(accounts[0])
      navigation.navigate('Confirmation', confirmation)
    } catch (error) {
      console.log(error);

      const errorMessage = prettyErrorMessage(error, 'gift', content)
      setMessage(errorMessage)

      setData({ loading: false, error })
      // form.setSubmitting(false)
    }
  }

  function handleTranslation(balance) {
    if (!balance || balance === undefined) {
      return ""
    } else if (balance.name === "Texts") {
      return content.swap.texts;
    } else if (balance.name === "Data") {
      return content.swap.data;
    } else if (balance.name === "Minutes") {
      return content.swap.minutes;
    }
  }

  function handleSuffixTranslation(balance) {
    if (!balance || balance === undefined) {
      return ""
    } else if (balance.suffix === "text(s)") {
      return content.swap.textsSuffix
    } else if (balance.suffix === "minute(s)") {
      return content.swap.minutesSuffix
    } else if (balance.suffix === "of data") {
      return content.swap.dataSuffix
    }
  }

  return (
    <KeyboardAwareScrollView contentContainerStyle={styles.container}>

      <Space height={24} />

      <View style={{alignItems: 'center'}}>
        <MoonDial
          size={192}
          name={handleTranslation(balance)}
          value={balance.value}
          range={balance.range}
          formatValue={balance.formatValueForDial}
          formatRange={balance.formatRange}
          style={styles.moondialStyle}
          strokeColour={colors.srGreen + 20}
          mostlyFullColour={colors.srDarkGreen}
          almostEmptyColour={colors.srGreen}
          mostlyFullTextColour={colors.srTextInput}
          almostEmptyTextColour={colors.srTextInput}
          textColour={colors.srTextInput}
					injectedContent={content}
          />
      </View>

      <Label>{content.swap.from}</Label>

      <Space height={12} />

      <TabSelector
        value={type}
        options={tabs.map(tab => ({
          ...tab,
          disabled: noTransferableBalances
        }))}
        onTabPress={handleTabChange}
        textStyle={styles.tabTextStyle}
      />

      <Space height={12} />

      <Slider
        value={minMaxLimits(slide, balance.min, balance.max)}
        step={1}
        minimumValue={balance.min}
        maximumValue={balance.max}
        onValueChange={handleSliderChange}
      />

      <Space height={12} />

      <TextInputExtended
        value={text}
        disabled={noTransferableBalances}
        onChangeText={handleTextChange}
        {...{[isValid ? 'footer' : 'error']: limits}}
        style={styles.inputStyle}
        footerStyle={styles.footerStyle}
        selectionColor={colors.common}
        textInputColour={styles.moondialTextStyle}
        {...testID('TransferAmount')}
      />

      <Space height={12} />
      {message && [
        <ErrorMessage key={0}>{message}</ErrorMessage>,
        <Space height={12} key={1}/>
      ]}
      <Button
        title={content.transfer.button}
        disabled={!isValid || loading}
        onPress={handleTransfer}
        titleStyle={{...typography.extrabold, fontSize: 18, color: colors.white}}
        disabledTitleStyle={{...typography.extrabold, fontSize: 18, color: colors.srDarkGreen + 45}}
        style={styles.enabled}
        disabledStyle={styles.disabled}
        {...testID('TransferSubmit')}
      />

      <Space height={12} />

      <View style={{alignItems: 'center'}}>
        <Text style={styles.result} {...testID('TransferResult')}>{result}</Text>
      </View>
    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 24,
    paddingBottom: 24,
    ...adaptive.wrapperPadding,
  },
  description: {
    textAlign: 'center',
    ...typography.lightbold
  },
  result: {
    ...typography.lightbold
  },
  moondialStyle: {
    ...typography.lightbold
  },
  enabled: {
    backgroundColor: colors.srDarkGreen 
  },
  disabled: {
    backgroundColor: colors.srDarkGreen + 60,
    borderColor: colors.srGreen,
    borderWidth: 1
  },
  tabTextStyle: {
    ...typography.lightbold
  },
  inputStyle: {
    ...typography.lightbold
  },
  footerStyle: {
    color: colors.focus
  },
  moondialTextStyle: {
    ...typography.lightbold,
    color: colors.srTextInput
  }
})
